import { useCallback, useEffect, useState } from 'react';
import useRealtimeSocket from './useRealtimeSocket';
import { useAuth } from './useAuth';

/**
 * Architecture Rule 74 — Subscribe to call lifecycle events on the shared
 * WebSocket connection and expose the current "incoming call" (if any).
 *
 * The HTTP backend publishes CALL_RINGING / CALL_PARTICIPANT_JOINED /
 * CALL_PARTICIPANT_LEFT / CALL_DECLINED / CALL_ENDED on the existing
 * `chat:{threadId}:events` Redis channel (Rule 20 + 71) — so we just
 * subscribe to every chat-thread room the user is in by using the
 * `subscribeAll` global handler. The chat page handles non-call events
 * via its own per-room subscription; this hook filters to call events.
 *
 * Usage:
 *   const { incoming, clear } = useIncomingCall();
 *   if (incoming) return <IncomingCallModal call={incoming} onClose={clear} />;
 *
 * Exposes a single `incoming` (most recent ringing call directed at this
 * user that they haven't accepted/declined). When the user is the starter
 * they're already in the call — no modal is shown.
 */
export default function useIncomingCall() {
  const { user } = useAuth();
  const { subscribeAll } = useRealtimeSocket();
  const [incoming, setIncoming] = useState(null);

  const clear = useCallback(() => setIncoming(null), []);

  useEffect(() => {
    if (!user?.id) return undefined;
    const handler = (payload) => {
      const type = String(payload?.type ?? '');

      // CALL_RINGING is the only event that opens the modal. The starter
      // is already in the room — don't notify themselves.
      if (type === 'CALL_RINGING') {
        if (Number(payload?.started_by) === Number(user.id)) return;
        setIncoming({
          callId:                    Number(payload?.call_id),
          threadId:                  Number(payload?.thread_id),
          kind:                      String(payload?.kind ?? 'voice'),
          startedBy:                 Number(payload?.started_by),
          startedByName:             String(payload?.started_by_name ?? ''),
          startedByUsername:         String(payload?.started_by_username ?? ''),
          // v1.76 — caller avatar signed by CallSessionService when the ring
          // is published. Empty string when the caller has no profile photo.
          startedByProfilePhotoUrl:  String(payload?.started_by_profile_photo_url ?? ''),
          roomName:                  String(payload?.room_name ?? ''),
          ringingExpiresAt:          payload?.ringing_expires_at ?? null,
        });
        return;
      }

      // Any termination event clears the modal so a missed ring doesn't
      // hang on screen forever.
      if (type === 'CALL_DECLINED' || type === 'CALL_ENDED' || type === 'CALL_PARTICIPANT_JOINED') {
        setIncoming((prev) => {
          if (!prev) return prev;
          if (Number(payload?.call_id) !== Number(prev.callId)) return prev;
          // If WE joined, clear (CallPage takes over). If someone else
          // declined a 1:1, also clear. If the call ended, clear.
          if (
            type === 'CALL_PARTICIPANT_JOINED'
              && Number(payload?.user_id) !== Number(user.id)
          ) {
            // Other party answered first (e.g. on another device) — modal stays
            // until WE answer or the ring expires. Treat as expired so we stop
            // surfacing it after a brief grace.
            return prev;
          }
          return null;
        });
      }
    };
    return subscribeAll(handler);
  }, [subscribeAll, user?.id]);

  // Auto-expire when ringing window elapses (defense in depth — the backend
  // also marks the call expired via a scheduled sweep, but the UI shouldn't
  // wait for that).
  useEffect(() => {
    if (!incoming?.ringingExpiresAt) return undefined;
    const expiresAt = new Date(incoming.ringingExpiresAt).getTime();
    const msLeft = Math.max(0, expiresAt - Date.now());
    const timer = setTimeout(() => setIncoming(null), msLeft + 250);
    return () => clearTimeout(timer);
  }, [incoming?.ringingExpiresAt]);

  return { incoming, clear };
}
